/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module HealthScoreModule
 */

import {
    AfterViewInit,
    Component,
    Inject,
    Input,
} from '@angular/core';

import {
    IHealthScoreDetails,
    IOperationalStatus,
} from 'generated-types';

import {
    Pool,
    Server,
    ServiceEngine,
    VirtualService,
} from 'object-types';

import {
    HttpMethod,
    HttpWrapper,
    IHttpWrapperRequestConfig,
    THttpWrapper,
} from 'ajs/modules/core/factories';

import { L10nService } from '@vmw/ngx-vip';
import { StringService } from 'string-service';
import { IHSSeries } from 'ng/modules/health-score/health-score.types';
import { IExtendedMetricsHealthScoreObj } from 'items/pool.item.factory';

import * as l10n from './avi-health-score-body.l10n';
import './avi-health-score-body.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;
const limit = 72;
const step = 300;

/**
 * @description Component for rendering avi health score body.
 * @author Nitesh Kesarkar, Deepak Chaudhary
 */
@Component({
    selector: 'avi-health-score-body',
    templateUrl: './avi-health-score-body.component.html',
})
export class AviHealthScoreBodyComponent implements AfterViewInit {
    @Input()
    public objectName: string;

    @Input()
    public uuid: string;

    @Input()
    public poolId: string;

    @Input()
    public operationStatus: IOperationalStatus;

    @Input()
    public healthScore: IExtendedMetricsHealthScoreObj;

    @Input()
    public limit: number;

    @Input()
    public step: number;

    @Input()
    public appSecurity = false;

    @Input()
    public appSecurityStatus = '';

    public readonly l10nKeys = l10nKeys;

    public hsSeries: IHSSeries;

    public isLoading = true;

    private readonly httpWrapper: HttpWrapper;

    constructor(
        public readonly l10nService: L10nService,
        public readonly stringService: StringService,
        @Inject(HttpWrapper)
        HttpWrapperClass: THttpWrapper,
    ) {
        this.httpWrapper = new HttpWrapperClass();

        l10nService.registerSourceBundles(dictionary);
    }

    public get objectNameValue(): boolean {
        const name = this.objectName?.toLowerCase();

        return name === VirtualService.toLowerCase();
    }

    /** @override */
    public ngAfterViewInit(): void {
        const url = '/api/analytics/healthscore/';
        const params = `summary=true&limit=${this.limit ?? limit}&step=${this.step ?? step}`;
        const objectName = this.objectName?.toLowerCase();

        switch (objectName) {
            case Pool.toLowerCase():
                this.fetchHsSeriesData(`${url}/pool/${this.uuid}?${params}`);
                break;
            case Server.toLowerCase():
                this.fetchHsSeriesData(`${url}/pool/${this.poolId}?${params}`);
                break;
            case VirtualService.toLowerCase():
                this.fetchHsSeriesData(`${url}/virtualservice/${this.uuid}?${params}`);
                break;
            case ServiceEngine.toLowerCase():
                this.fetchHsSeriesData(`${url}/serviceengine/${this.uuid}?${params}`);
                break;
            default:
                this.isLoading = false;
        }
    }

    public async fetchHsSeriesData(url: string): Promise<void> {
        const requestConfig: IHttpWrapperRequestConfig = {
            url,
            method: HttpMethod.GET,
        };

        const response = await this.httpWrapper.request(requestConfig);
        const { series } = response.data;
        const [{ data }] = series;

        this.hsSeries = {
            values: data.map((d: IHealthScoreDetails) => ({
                timestamp: Date.parse(d.timestamp),
                value: d.value,
            })),
        };
        this.isLoading = false;
    }
}
